import { useState, useEffect } from "react";
import {
  TrendingUp, TrendingDown, DollarSign, Users, Image, Calendar,
  BarChart3, PieChart, Download, Loader2, RefreshCw, ChevronDown,
  Lightbulb, AlertTriangle, CheckCircle2, Info, ArrowUpRight
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "../../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import { useFeatureGate } from "../../hooks/useFeatureGate";
import FeatureGate from "../../components/FeatureGate";

const AnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");
  const [overview, setOverview] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [galleries, setGalleries] = useState(null);
  const [topPerformers, setTopPerformers] = useState(null);
  const [insights, setInsights] = useState([]);
  const [exporting, setExporting] = useState(false);
  
  // Feature gating
  const { hasFeature } = useFeatureGate();
  const hasAnalytics = hasFeature("analytics");

  useEffect(() => {
    // Only fetch data if user has analytics feature
    if (hasAnalytics) {
      fetchAllData();
    } else {
      setLoading(false);
    }
  }, [period, hasAnalytics]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [overviewRes, revenueRes, bookingsRes, galleriesRes, performersRes, insightsRes] = await Promise.all([
        axios.get(`${API}/analytics/overview?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics/revenue?period=${period}&group_by=day`, { withCredentials: true }),
        axios.get(`${API}/analytics/bookings?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics/galleries?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics/top-performers?period=${period}`, { withCredentials: true }),
        axios.get(`${API}/analytics/insights`, { withCredentials: true })
      ]);

      setOverview(overviewRes.data);
      setRevenue(revenueRes.data);
      setBookings(bookingsRes.data);
      setGalleries(galleriesRes.data);
      setTopPerformers(performersRes.data);
      setInsights(insightsRes.data.insights || []);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      if (error.response?.status === 403) {
        toast.error("Analytics requires a Studio plan");
      } else {
        toast.error("Failed to load analytics");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(
        `${API}/analytics/export?period=${period}&format=csv`,
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `analytics_${period}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export downloaded");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case "success": return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "warning": return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case "opportunity": return <Lightbulb className="w-5 h-5 text-blue-500" />;
      default: return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Wrap entire page in feature gate for Pro+ users
  if (!hasAnalytics) {
    return (
      <div className="space-y-6" data-testid="analytics-dashboard">
        <div>
          <h1 className="text-2xl font-semibold">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Track your business performance and revenue
          </p>
        </div>
        <FeatureGate feature="analytics">
          {/* Placeholder content shown in locked state */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 h-[400px]">
            <Card><CardHeader><CardTitle className="text-sm">Revenue</CardTitle></CardHeader></Card>
            <Card><CardHeader><CardTitle className="text-sm">Bookings</CardTitle></CardHeader></Card>
            <Card><CardHeader><CardTitle className="text-sm">Galleries</CardTitle></CardHeader></Card>
            <Card><CardHeader><CardTitle className="text-sm">Clients</CardTitle></CardHeader></Card>
          </div>
        </FeatureGate>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Track your business performance and revenue
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
              <SelectItem value="ytd">Year to date</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchAllData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button onClick={handleExport} disabled={exporting}>
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      {overview && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card data-testid="total-revenue-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Revenue
              </CardTitle>
              <DollarSign className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(overview.summary.total_revenue)}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Bookings: {formatCurrency(overview.summary.booking_revenue)} · 
                Downloads: {formatCurrency(overview.summary.download_revenue)}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="bookings-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Confirmed Bookings
              </CardTitle>
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {overview.summary.total_bookings}
              </div>
              <div className="flex items-center text-xs mt-1">
                {overview.summary.booking_change_percent > 0 ? (
                  <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
                ) : overview.summary.booking_change_percent < 0 ? (
                  <TrendingDown className="w-3 h-3 text-red-500 mr-1" />
                ) : null}
                <span className={overview.summary.booking_change_percent > 0 ? "text-green-500" : overview.summary.booking_change_percent < 0 ? "text-red-500" : "text-muted-foreground"}>
                  {overview.summary.booking_change_percent > 0 ? "+" : ""}{overview.summary.booking_change_percent}%
                </span>
                <span className="text-muted-foreground ml-1">vs previous period</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="conversion-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Conversion Rate
              </CardTitle>
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {overview.summary.conversion_rate}%
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {overview.summary.pending_bookings} pending requests
              </p>
            </CardContent>
          </Card>

          <Card data-testid="galleries-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Galleries Delivered
              </CardTitle>
              <Image className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {overview.summary.galleries_delivered}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                This period
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              Business Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {insights.map((insight, idx) => (
                <div 
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    insight.type === "success" ? "bg-green-500/10" :
                    insight.type === "warning" ? "bg-yellow-500/10" :
                    insight.type === "opportunity" ? "bg-blue-500/10" :
                    "bg-muted/50"
                  }`}
                >
                  {getInsightIcon(insight.type)}
                  <div className="flex-1">
                    <p className="font-medium">{insight.title}</p>
                    <p className="text-sm text-muted-foreground">{insight.message}</p>
                  </div>
                  {insight.metric && (
                    <Badge variant="outline" className="ml-auto">
                      {insight.metric}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue Chart */}
      {revenue && revenue.time_series.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Revenue Over Time</CardTitle>
            <CardDescription>
              Total: {formatCurrency(revenue.totals.total_revenue)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-end gap-1">
              {revenue.time_series.slice(-30).map((point, idx) => {
                const maxValue = Math.max(...revenue.time_series.map(p => p.total_revenue));
                const height = maxValue > 0 ? (point.total_revenue / maxValue * 100) : 0;
                return (
                  <div 
                    key={idx}
                    className="flex-1 bg-primary/80 hover:bg-primary rounded-t transition-colors relative group"
                    style={{ height: `${Math.max(height, 2)}%` }}
                  >
                    <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block bg-popover border rounded px-2 py-1 text-xs whitespace-nowrap z-10">
                      <p className="font-medium">{formatCurrency(point.total_revenue)}</p>
                      <p className="text-muted-foreground">{point.period}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two Column Layout */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Booking Funnel */}
        {bookings && (
          <Card>
            <CardHeader>
              <CardTitle>Conversion Funnel</CardTitle>
              <CardDescription>
                From request to confirmed booking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {bookings.conversion_funnel.map((stage, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{stage.stage}</span>
                      <span className="text-muted-foreground">{stage.count} ({stage.percent}%)</span>
                    </div>
                    <div className="h-3 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${stage.percent}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t">
                <p className="text-sm font-medium mb-2">Average Booking Value</p>
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(bookings.summary.average_booking_value)}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Top Event Types */}
        {topPerformers && (
          <Card>
            <CardHeader>
              <CardTitle>Top Event Types</CardTitle>
              <CardDescription>
                By revenue this period
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {topPerformers.top_event_types.map((event, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{event.event_type}</p>
                      <p className="text-sm text-muted-foreground">{event.count} bookings</p>
                    </div>
                    <p className="font-semibold">{formatCurrency(event.revenue)}</p>
                  </div>
                ))}
                {topPerformers.top_event_types.length === 0 && (
                  <p className="text-muted-foreground text-center py-4">
                    No data for this period
                  </p>
                )}
              </div>

              <div className="mt-6 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  Repeat Client Rate: <span className="font-semibold text-foreground">{topPerformers.repeat_client_rate}%</span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Gallery Stats */}
      {galleries && (
        <Card>
          <CardHeader>
            <CardTitle>Gallery Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-3xl font-bold">{galleries.summary.total_galleries}</p>
                <p className="text-sm text-muted-foreground">Total Galleries</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-3xl font-bold">{galleries.summary.delivered}</p>
                <p className="text-sm text-muted-foreground">Delivered</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-3xl font-bold">{galleries.summary.total_photos}</p>
                <p className="text-sm text-muted-foreground">Total Photos</p>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <p className="text-3xl font-bold">{formatCurrency(galleries.summary.download_revenue)}</p>
                <p className="text-sm text-muted-foreground">Download Sales</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top Clients */}
      {topPerformers && topPerformers.top_clients.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Top Clients</CardTitle>
            <CardDescription>
              Highest value clients this period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium text-muted-foreground">Client</th>
                    <th className="text-center py-2 font-medium text-muted-foreground">Bookings</th>
                    <th className="text-right py-2 font-medium text-muted-foreground">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topPerformers.top_clients.map((client, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-3">
                        <p className="font-medium">{client.name}</p>
                        <p className="text-sm text-muted-foreground">{client.email}</p>
                      </td>
                      <td className="text-center py-3">{client.count}</td>
                      <td className="text-right py-3 font-semibold">
                        {formatCurrency(client.revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
